import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { CityRequestForm } from '../components/CityRequestForm';
import './Home.css';

interface AdventureCard {
  id: string;
  emoji: string;
  title: string;
  subtitle: string;
  color: string;
  route: string;
}

const adventures: AdventureCard[] = [
  { id: 'events', emoji: '🎉', title: 'Events', subtitle: 'Fun things happening this weekend', color: '#FF6B6B', route: '/events' },
  { id: 'birthday', emoji: '🎂', title: 'Birthday Party Venues', subtitle: 'Amazing places to celebrate', color: '#4ECDC4', route: '/birthday-parties' },
  { id: 'classes', emoji: '🎓', title: 'Classes', subtitle: 'Year-round music, sports & martial arts', color: '#A8E6CF', route: '/classes' },
  { id: 'sports', emoji: '⚽', title: 'Sports & Activities', subtitle: 'Soccer, baseball, basketball & more', color: '#FFD3B6', route: '/sports' },
  { id: 'camps', emoji: '🏕️', title: 'Camps', subtitle: 'Winter, spring & summer programs', color: '#95E1D3', route: '/camps' },
  { id: 'attractions', emoji: '🎢', title: 'Attractions', subtitle: 'Zoos, museums & fun places to visit', color: '#FFDAB9', route: '/attractions' },
  { id: 'school-districts', emoji: '🏫', title: 'School District', subtitle: 'Calendars, holidays & important dates', color: '#C7CEEA', route: '/school-districts' }
];

const cities = ['Houston', 'The Woodlands'];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { preferences, loading: preferencesLoading } = useUserPreferences();
  const [selectedCity, setSelectedCity] = useState<string>('Houston');

  useEffect(() => {
    const preferencesPrompted = localStorage.getItem(`preferences_prompted_${user?.id}`);
    if (!authLoading && !preferencesLoading && user && !preferences && !preferencesPrompted) {
      localStorage.setItem(`preferences_prompted_${user.id}`, 'true');
      navigate('/preferences');
    }
  }, [user, preferences, authLoading, preferencesLoading, navigate]);

  useEffect(() => {
    if (preferences?.city) {
      setSelectedCity(preferences.city);
      localStorage.setItem('selectedCity', preferences.city);
    } else {
      const savedCity = localStorage.getItem('selectedCity');
      if (savedCity) setSelectedCity(savedCity);
    }
  }, [preferences]);

  const handleCityChange = (city: string) => {
    setSelectedCity(city);
    localStorage.setItem('selectedCity', city);
  };

  return (
    <div className="home-container">
      <div className="location-selector">
        <div className="location-icon">📍</div>
        <div className="location-content">
          <label htmlFor="city-select" className="location-label">Your Location</label>
          <select id="city-select" value={selectedCity} onChange={(e) => handleCityChange(e.target.value)} className="city-dropdown">
            {cities.map((city) => (<option key={city} value={city}>{city}</option>))}
          </select>
        </div>
      </div>

      {!user && (
        <div className="benefits-banner">
          <div className="benefits-content">
            <h3>🎯 Get More with a Free Account</h3>
            <div className="benefits-list">
              <div className="benefit-item">❤️ Save your favorite events</div>
              <div className="benefit-item">📍 See events closest to you</div>
              <div className="benefit-item">🎂 Filter by your kids' ages</div>
            </div>
            <button className="benefits-cta" onClick={() => navigate('/auth-callback')}>
              Sign Up Free
            </button>
          </div>
        </div>
      )}

      <div className="home-header"><h1>What are you looking for today?</h1></div>
      <div className="adventure-cards">
        {adventures.map((adventure) => (
          <div key={adventure.id} className="adventure-card" style={{ backgroundColor: adventure.color }}
            onClick={() => navigate(adventure.route)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(adventure.route); }}>
            <div className="card-emoji">{adventure.emoji}</div>
            <div className="card-content">
              <h2 className="card-title">{adventure.title}</h2>
              <p className="card-subtitle">{adventure.subtitle}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="how-it-works">
        <h2>How Jollyio Works</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-icon">🔍</div>
            <h3>Browse Events</h3>
            <p>Explore 1000+ kid-friendly activities in Houston metro</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-icon">❤️</div>
            <h3>Save Favorites</h3>
            <p>Bookmark events you don't want to miss</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <div className="step-icon">🎉</div>
            <h3>Never Miss Out</h3>
            <p>Get personalized recommendations for your family</p>
          </div>
        </div>
      </div>

      <CityRequestForm />
    </div>
  );
};

export default Home;
